/** Skin-tone modifiers (👍🏽 → 👍). */
const SKIN_TONE_RE = /[\u{1F3FB}-\u{1F3FF}]/gu;
/** Variation selector-16 (✔️ → ✔). */
const VS16_RE = /\uFE0F/g;

/** Confirm / decline emoji allowlist — base glyphs after normalization. */
export const CONFIRM_EMOJI = new Set(['👍', '✅', '✔', '☑', '👌']);
export const DECLINE_EMOJI = new Set(['👎', '❌', '✖', '🚫']);

export const CONFIRM_TEXT_TOKENS = new Set([
  '1',
  '١',
  'yes',
  'y',
  'ok',
  'okay',
  'confirm',
  'confirmed',
  'sure',
  'yep',
  'yeah',
  'yup',
  'correct',
  'proceed',
  'approve',
  'approved',
  'go ahead',
  'do it',
  'yes please',
  'yes sure',
  'sure thing',
  'sounds good',
  'please do',
  'ok go ahead',
  'yes confirm',
  'confirm it',
  'نعم',
  'أكد',
  'اكد',
  'أكيد',
  'اكيد',
  'تأكيد',
  'تاكيد',
  'ايه',
  'اي',
  'أجل',
  'اجل',
  'ايوه',
  'ايوة',
  'أيوه',
  'تمام',
  'تم',
  'طيب',
  'ماشي',
  'موافق',
  'موافقة',
  'اوك',
  'أوك',
  'اوكي',
  'يلا',
  'صح',
  'نعم أكد',
  'نعم اكد',
  'طيب أكد',
  'طيب اكد',
  'تمام أكد',
  'تمام اكد',
  'haan',
  'han',
  'ji',
  'jee',
  'ji haan',
  'jee haan',
  'haan ji',
  'theek hai',
  'theek',
  'bilkul',
  'zaroor',
  'kar do',
  'karo',
  'ہاں',
  'ہاں جی',
  'جی',
  'جی ہاں',
  'ٹھیک ہے',
  'ٹھیک',
  'بالکل',
  'ضرور',
  'کر دو',
  'کر دیں',
]);

export const DECLINE_TEXT_TOKENS = new Set([
  '2',
  '٢',
  'no',
  'n',
  'nope',
  'nah',
  'cancel',
  'decline',
  'stop',
  "don't",
  'dont',
  'no thanks',
  'no thank you',
  'not now',
  'never mind',
  'nevermind',
  'skip',
  'skip it',
  'cancel it',
  "don't do it",
  'dont do it',
  'لا',
  'إلغاء',
  'الغاء',
  'رفض',
  'كلا',
  'لا شكرا',
  'لا شكراً',
  'لا أريد',
  'لا اريد',
  'مو الحين',
  'ليس الآن',
  'ليس الان',
  'لغيه',
  'الغيه',
  'الغه',
  'وقف',
  'توقف',
  'خلاص لا',
  'nahin',
  'nahi',
  'na',
  'nahin chahiye',
  'nahi chahiye',
  'cancel karo',
  'rehne do',
  'rehnay do',
  'mat karo',
  'ruko',
  'نہیں',
  'نہیں چاہیے',
  'منسوخ',
  'منسوخ کریں',
  'رہنے دو',
  'مت کرو',
  'رکو',
]);

/** Strip skin-tone modifiers + VS16 so 👍🏽 and ✔️ normalize consistently. */
export const normalizeInboundReply = (text: string): string =>
  text.trim().replace(VS16_RE, '').replace(SKIN_TONE_RE, '').trim();

const EMOJI_ALLOWLIST_RE =
  /[\u{1F44D}\u{1F44E}\u{2705}\u{2714}\u{2611}\u{1F44C}\u{274C}\u{2716}\u{1F6AB}]/gu;

/** Remove allowlisted confirm/decline emoji (+ modifiers already stripped). */
export const stripAllowlistedEmoji = (text: string): string =>
  normalizeInboundReply(text).replace(EMOJI_ALLOWLIST_RE, '').trim();

const emojiOnlyDecision = (normalized: string): 'confirm' | 'decline' | null => {
  if (!normalized) return null;
  const segments = [...normalized];
  if (segments.length === 0) return null;
  const allConfirm = segments.every(ch => CONFIRM_EMOJI.has(ch));
  const allDecline = segments.every(ch => DECLINE_EMOJI.has(ch));
  if (allConfirm && segments.length > 0) return 'confirm';
  if (allDecline && segments.length > 0) return 'decline';
  return null;
};

/**
 * Deterministic yes/no for widget-less confirmation (text + emoji-only).
 * Long or ambiguous messages return `null` (full agent turn).
 */
export const interpretInboundConfirmationReply = (
  text: string,
): 'confirm' | 'decline' | null => {
  // Merged confirmation bursts. When several queued taps ("1", then "نعم")
  // land while a turn is in flight, the turn coordinator folds them into a
  // single newline-joined string ("1\nنعم"). Left as-is that no longer
  // matches any single token and would dead-end into a full LLM turn, so
  // collapse a multi-line message to one decision when every line agrees.
  const rawTrimmed = text.trim();
  if (rawTrimmed.includes('\n')) {
    const lines = rawTrimmed
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean);
    if (lines.length > 1) {
      const decisions = lines.map(line => interpretInboundConfirmationReply(line));
      if (decisions.every(d => d === 'confirm')) return 'confirm';
      if (decisions.every(d => d === 'decline')) return 'decline';
      return null;
    }
  }

  const trimmed = rawTrimmed.replace(/[.!؟?،,]+$/u, '').trim();
  if (!trimmed || trimmed.length > 30) return null;

  const normalized = normalizeInboundReply(trimmed);
  const emojiDecision = emojiOnlyDecision(normalized);
  if (emojiDecision) return emojiDecision;

  const lower = trimmed.toLowerCase().replace(/\s+/g, ' ');
  if (CONFIRM_TEXT_TOKENS.has(lower)) return 'confirm';
  if (DECLINE_TEXT_TOKENS.has(lower)) return 'decline';
  return null;
};

/**
 * WhatsApp account-type button taps are remapped to these Arabic
 * funnel lines so tools still match. They are not the user speaking
 * Arabic — treating them as a language demonstration flipped an Urdu
 * thread to Arabic (runtime: Driver apni docs → سائق).
 */
export const ACCOUNT_TYPE_PICK_TEXT = {
  carrier: 'شركة نقل',
  broker: 'شركة وسيط شحن',
  freelancer: 'سائق (الوثائق لك أنت)',
} as const;

const ACCOUNT_TYPE_PICKS = new Set<string>(Object.values(ACCOUNT_TYPE_PICK_TEXT));

export const isAccountTypeFunnelPick = (text: string): boolean =>
  ACCOUNT_TYPE_PICKS.has(text.trim());

/**
 * Truck-body answers (Arabic or Latin slang). Not a language choice —
 * treating «تريلا سطحة» as Arabic flipped an Urdu thread (runtime).
 */
const TRUCK_TYPE_TOKEN =
  /تريلا|سطحة|ستارة|ستاره|دينا|مبرد|مغلقة|flatbed|curtain|reefer|closed\s*body|traila|satha|sitara|dyna/gi;

export const isTruckTypeOnlyInbound = (text: string): boolean => {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 80) return false;
  const leftover = trimmed
    .replace(TRUCK_TYPE_TOKEN, '')
    .replace(/[\s,،.+:;!?؟-]+/g, '');
  return (
    leftover.length === 0 &&
    /تريلا|سطحة|ستارة|ستاره|دينا|مبرد|مغلقة|flatbed|curtain|reefer|closed\s*body|traila|satha|sitara|dyna/i.test(
      trimmed,
    )
  );
};

/**
 * Two-city lane in Latin or Arabic ("Riyadh to jeddah"). Place names are
 * not a language switch — treating that as English flipped an Urdu thread.
 */
const LANE_CITY_SOURCE =
  'riyadh|jeddah|dammam|khobar|mecca|makkah|medina|madinah|abha|tabuk|hail|qassim|buraydah|najran|jazan|jizan|jubail|yanbu|taif|ahsa|hofuf|الرياض|رياض|جدة|جده|الدمام|دمام|الخبر|مكة|مكه|المدينة|أبها|ابها|تبوك|حائل|حايل|بريدة|القصيم|نجران|جازان|جيزان|الجبيل|ينبع|الطائف|الاحساء|الأحساء';

const LANE_CONNECTOR_SOURCE = '\\b(to|from|se|le|ila|toward|towards)\\b|إلى|الى|من|➡️|→|->';

export const isLaneOnlyInbound = (text: string): boolean => {
  const trimmed = text.trim();
  if (!trimmed || trimmed.length > 80) return false;
  const cityHits = trimmed.match(new RegExp(LANE_CITY_SOURCE, 'gi')) ?? [];
  if (cityHits.length < 2) return false;
  const leftover = trimmed
    .replace(new RegExp(LANE_CITY_SOURCE, 'gi'), ' ')
    .replace(new RegExp(LANE_CONNECTOR_SOURCE, 'gi'), ' ')
    .replace(/[\s,،.+:;!?؟-]+/g, '');
  return leftover.length === 0;
};

/** True when the message is a short yes/no, emoji, remapped account tap, truck type, or a city-only lane. */
export const isLanguageNeutralInbound = (text: string): boolean =>
  interpretInboundConfirmationReply(text) !== null ||
  isAccountTypeFunnelPick(text) ||
  isTruckTypeOnlyInbound(text) ||
  isLaneOnlyInbound(text);
